/**
 * Playing the engine, on the board the famous games are played on.
 *
 * The board itself is `table.js`, which `vs-friend.html` uses too — the scene, the
 * clicking, the score, the tray, the camera and the theme are all the same thing
 * on both pages. What is here is only what is particular to playing a MACHINE: the
 * ladder of historical opponents, handing the position to a search running in a
 * worker, and what to offer somebody who has just beaten Morphy.
 *
 * WHOSE TURN IT IS
 * The table does not know, and asks. `canPick` is what stops you moving during the
 * opponent's think, and `onPick` is how a chosen move gets here — the table does
 * not play it, this does, which is the same shape the friend page uses for a very
 * different reason.
 */

import { createTable, escapeHtml } from './table.js';
import { restFrame, uciOf } from './live-game.js';
import { LADDER, LEVELS, rungById, Opponent } from './opponent.js';
import { bookMove, loadBook } from './book.js';
import { isSimple, onMode } from '../view-mode.js';
import { readPlayOn, positionFor } from './from-here.js';
import { keepGame, copyPgn } from './keep.js';
import { BY_ID, watchUrl } from '../data/games-index.js';
import { mountNav } from '../nav.mjs';
import { mountInstall } from '../install.js';
import { mountCameraMenu, CAMERA_WORDS } from '../camera-menu.js';
import { mountLeanLayout } from '../lean-layout.js';
import { count } from '../analytics.js';
import { read as kept, syncSoon, write as keep } from '../progress.js';
import {
  allGames, getGame, newGameId, reviewUrl, saveGame, syncGames,
} from '../data/my-games.js';
import { showLevel } from './level-line.js';
import { RUNG_LEVEL } from '../level.js';

/**
 * A rung on the level scale (`../level.js`): what beating it regularly makes
 * you. The Elo it is told to play at stays in the tooltip for those who think
 * in Elo.
 */
const rungLevel = (entry) => (RUNG_LEVEL[entry.id] != null
  ? `<span class="rung-elo" title="${escapeHtml(entry.eloEst)}">Level ${RUNG_LEVEL[entry.id].toFixed(1)}</span>` : '');
import { replay } from '../../animations/207-famous-chess-games/chess.js';

const $ = (id) => document.getElementById(id);

/**
 * WHERE A GAME IN PROGRESS IS KEPT, so that closing the tab is not resigning.
 *
 * WHY IT WAS WORTH ADDING. The friend page has never had this problem: the score
 * lives in a Durable Object, so both players can shut the laptop and come back
 * tomorrow to the same link. The engine game — the commoner of the two by far —
 * held its score in a variable and nowhere else. A refresh, a phone locking, a
 * browser deciding to reclaim a background tab, and thirty moves against Morphy
 * were gone with no way to ask for them back.
 *
 * WHAT IS STORED IS THE SCORE AND NOTHING DERIVED. Four fields: the opponent, the
 * side you took, the moves in UCI, and when. The position is not stored, the
 * captured men are not stored, the camera is not stored — every one of those is
 * something `table.setScore` recomputes from the moves, and a stored copy of a
 * derived thing is a second source of truth that can disagree with the first.
 * Replaying twenty moves takes a millisecond; the bug where the board and the
 * move list disagree about what happened takes an afternoon.
 *
 * `localStorage` and not IndexedDB, unlike a game you have KEPT. This is a
 * scratchpad holding one game that is expected to be overwritten or deleted
 * within the hour, and it is written after every half-move — a synchronous write
 * of a few hundred bytes is the right tool, and `imported.js`'s store is the
 * right one for a game somebody has decided to keep. The two do not compete: this
 * is cleared the moment a game ends, and that is when `keep.js` is offered.
 */
const SAVED_KEY = 'immortal-in-progress';

/** Which rung of the ladder is chosen. A preference, so it merges newest-wins. */
const RUNG_KEY = 'immortal-rung';

/** How long an unfinished game is still worth offering. */
const SAVED_DAYS = 14;

/**
 * Write the game in progress, or clear it. Never throws: private mode has no store.
 *
 * Kept here and, for a signed-in visitor, on the account — see
 * `chess-app/progress.js`. This one merges NEWEST-WINS rather than by union:
 * two devices each holding a half-played game against the engine cannot be
 * combined into one board, and the one you touched last is the one you meant.
 *
 * Cleared as an empty string rather than removed, because a removal has nothing
 * to carry a timestamp and so cannot beat a stale copy on the server. `savedGame`
 * below reads an empty string as "no game", which it already did for anything it
 * could not parse.
 */
function saveProgress() {
  recordGame();
  if (phase !== 'yours' && phase !== 'theirs') {
    keep(SAVED_KEY, '');
    return;
  }
  keep(SAVED_KEY, JSON.stringify({
    rung: rung.id, you, moves: live.uci().slice(0, live.ply), at: Date.now(), id: gameId,
  }));
}

/**
 * This game, in the list of every game you have played here — see
 * `../data/my-games.js`. Written with the save above, after every move and once
 * more at the end, so a game is in your history whether or not anybody pressed
 * anything. The save above is the one game to offer back on this page; this is
 * all of them, to review or to continue from the games page.
 */
function recordGame() {
  // An empty board is not stored as a game, but a stored one is brought back to
  // it: taking every move back is still that game (`saveGame` knows which).
  if (!gameId) return;
  const over = phase === 'over';
  const out = over ? live.outcome : null;
  saveGame({
    id: gameId,
    kind: 'engine',
    you,
    rung: rung.id,
    white: you === 'w' ? 'You' : rung.name,
    black: you === 'w' ? rung.name : 'You',
    moves: live.game.moves.slice(0, live.ply),
    result: out?.result || '*',
    by: out?.by || '',
    over,
    from: takenUp ? { game: takenUp.id, title: takenUp.title, moveNo: takenUp.moveNo } : null,
    takebacks: tookBack || undefined,
  });
}

/**
 * The saved game, if there is one worth offering — otherwise null.
 *
 * Everything is checked rather than trusted, because `localStorage` is a text
 * field any visitor can edit and a stale one can outlive a release that renamed a
 * rung. An unreadable save is silently dropped: there is nothing a visitor could
 * do about it and nothing lost that they still had.
 */
function readProgress() {
  try {
    const raw = kept(SAVED_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!saved || !Array.isArray(saved.moves) || !saved.moves.length) return null;
    if (saved.you !== 'w' && saved.you !== 'b') return null;
    if (!rungById(saved.rung) || rungById(saved.rung).id !== saved.rung) return null;
    if (!(typeof saved.at === 'number') || Date.now() - saved.at > SAVED_DAYS * 864e5) return null;
    if (!saved.moves.every((m) => typeof m === 'string' && /^[a-h][1-8][a-h][1-8][qrbn]?$/.test(m))) return null;
    return saved;
  } catch {
    return null;
  }
}

// The sections, as a menu, into the slot the masthead holds for it. Before
// anything else this file does, and in particular before the wait for three.js:
// somebody who lands here and would rather be somewhere else should not have to
// wait for a WebGL context to find out where else there is.
mountNav($('nav-slot'), { here: 'play', base: '../' });
mountInstall($('install-slot'), { base: '../' });

/**
 * The setup screen's two steps: WHO (the computer, a friend, people online, and
 * an unfinished game) and, for the computer, HOW STRONG and which colour. A
 * friend and people online are links to their own pages. See `#setup`.
 *
 * Also before the wait for three.js, for the same reason as the menu: the first
 * step is page rather than scene, and it covers the board, so it is on screen at
 * once and the board builds behind it. Pick the computer before the board is
 * there and the boot screen shows until it is (`.boot` in play.css).
 */
function showStep(which) {
  $('step-who').hidden = which !== 'who';
  $('step-computer').hidden = which !== 'computer';
  $('setup').scrollTop = 0;
}
$('choose-computer').addEventListener('click', () => {
  showStep('computer');
  $('levels').querySelector('.on')?.focus({ preventScroll: true });
});
// The masthead's back arrow steps back through the setup before it leaves the
// page: from the levels to who you are playing, and only from there out. In the
// capture phase, so it gets there before the arrow's own `history.back()`.
document.addEventListener('click', (event) => {
  if (!event.target.closest?.('.nav-back')) return;
  if ($('setup').hidden || $('step-computer').hidden) return;
  event.preventDefault();
  event.stopPropagation();
  showStep('who');
  $('choose-computer').focus({ preventScroll: true });
}, true);

const table = await createTable();
// The six angles as one button over the board, named for where they look from.
// See `../camera-menu.js`.
mountCameraMenu($('views'), { labels: CAMERA_WORDS });
// 2D / 3D to the masthead and full screen to the bar (`../lean-layout.js`).
mountLeanLayout();
const live = table.live;

/** setup | yours | theirs | over */
let phase = 'setup';
let you = 'w';
let rung = rungById(kept(RUNG_KEY) || 'anderssen');
/** This game's id in `my-games.js`. A new one per game; a resumed game keeps its own. */
let gameId = null;

/**
 * Put this device's state on the account, and take down what is already there.
 *
 * NOT AWAITED, and the offer to resume is deliberately not held back for it. The
 * saved game is read and offered from `localStorage` while the page boots, which
 * is what keeps this page working signed out and on a bad connection. What the
 * sync does on a browser that has never seen this account is fill the store, so
 * the game is offered from the NEXT visit — a beat late rather than a spinner in
 * front of a board. See `chess-app/progress.js`.
 */
syncSoon();
syncGames();
let opponent = null;
let thinking = false;
/**
 * The position out of a famous game this page was opened on, if it was.
 *
 * Set once, by `takeUp` below, and read afterwards only to SAY where the game
 * came from — the pills, and the offer at the end to go back and try it again.
 * The score itself is not kept here: it went onto the board through
 * `table.setScore` like every other score, and a second copy of it would be a
 * second thing that can disagree with the board.
 */
let takenUp = null;

/**
 * How many moves this game has taken back. A game with any is kept like every
 * other, and earns its points, but does not count towards your level: a
 * measurement of how you play is not one of how you play with a rewind button.
 * `ratedGame` in `../level.js` reads it off the record.
 */
let tookBack = 0;

table.canPick = () => phase === 'yours' && !thinking;
table.onPick = (mv) => makeMove(mv);

// -------------------------------------------------------------------- the game

/** Play a move: onto the board, then over to the other side. */
async function makeMove(mv) {
  await table.apply(mv);
  if (live.outcome) return finish();
  phase = phase === 'yours' ? 'theirs' : 'yours';
  // After the move is on the board and before the engine is asked to think: a
  // crash during the think is the case this is for, and a save that happens
  // afterwards would not cover it.
  saveProgress();
  reflect();
  if (phase === 'theirs') theirTurn();
}

/**
 * The opponent's move.
 *
 * The whole score goes over on every move rather than just the new one — UCI has
 * no way to say "and then this", and a search that has been told the history is a
 * search that can see a repetition coming.
 */
async function theirTurn() {
  if (!opponent || live.outcome) return;
  thinking = true;
  reflect();
  // The opening, from the legend's own games, while the position is one they
  // reached with the move. See `book.js`.
  const fromBook = await playFromBook();
  if (fromBook) return fromBook;
  let answer;
  try {
    answer = await opponent.think(live.uci(), {
      onInfo: (info) => {
        // Depth is the honest thing to show while it works: an evaluation
        // flickering between +0.3 and -0.1 twenty times a second is noise being
        // presented as information.
        if (info.depth) table.say(`${rung.name} is thinking — depth ${info.depth}`);
      },
    });
  } catch (err) {
    thinking = false;
    table.say(`The engine stopped: ${err.message}`);
    return;
  }
  thinking = false;
  if (!answer.best) return finish();

  const mv = live.fromUci(answer.best);
  if (!mv) {
    // Has never happened and would mean the engine and the generator disagree
    // about the position, which is worth saying out loud rather than ignoring.
    table.say(`The engine offered ${answer.best}, which is not legal here. The game is stopped.`);
    phase = 'over';
    reflect();
    return;
  }
  table.say('');
  await makeMove(mv);
}

/**
 * A move out of the rung's book, played with a moment's pause so it does not
 * look like the machine it is not. Says where it came from.
 */
async function playFromBook() {
  const asked = rung;
  const book = await loadBook(asked.game ? asked.id : null);
  if (asked !== rung || !book || live.outcome) return null;
  const pick = bookMove(book, live.pos, { ply: live.ply });
  if (!pick) return null;
  const mv = live.fromUci(pick.uci);
  if (!mv) return null;
  const san = live.pos.san(mv);
  const surname = asked.name.split(' ').pop();
  await new Promise((resolve) => { setTimeout(resolve, Math.min(900, Math.max(350, asked.ms * 0.8))); });
  if (asked !== rung || phase !== 'theirs') return null;
  thinking = false;
  table.say(pick.total > 1
    ? `${san}, from ${surname}'s own games: played here in ${pick.n} of ${pick.total}.`
    : `${san}, as ${surname} played it.`);
  count('play-book', asked.id, live.ply);
  await makeMove(mv);
  return true;
}

/** It is over. */
function finish() {
  phase = 'over';
  table.state = 'over';
  table.putDown();
  const out = live.outcome || { result: '*', by: 'stopped', winner: null };
  // The result is deliberately not written onto the game object, which stays `*`
  // for the life of the page — `report()` in the scene reads it to tell a score
  // still being played from a published one, and every line it prints is about a
  // finished video. What the result is is said here instead.
  if (out.by === 'checkmate') table.explainMate(true);

  const won = out.winner === you;
  const drew = !out.winner;
  // Which rung people actually get past, and whether a game gets finished at all.
  // The number is half-moves, so a game that ended on move 20 reports 39 or 40 —
  // `live.ply` rather than a count of the moves list, since that is the one the
  // generator maintains.
  count('play-end', out.by === 'you resigned' ? 'resigned'
    : drew ? 'drawn' : won ? 'won' : 'lost', live.ply);
  $('over-line').textContent = drew
    ? `Drawn — ${out.result}`
    : won ? `You won — ${out.result}` : `${rung.name} won — ${out.result}`;
  $('over-why').textContent = capitalise(out.by) + '.';

  const acts = $('over-acts');
  acts.innerHTML = '';
  // Beating somebody has somewhere to go: their actual game, in the library.
  const beaten = rung.game ? BY_ID.get(rung.game) : null;
  if (won && beaten) {
    const link = document.createElement('a');
    link.className = 'ctl primary';
    link.href = watchUrl(rung.game);
    link.textContent = `Watch the real ${beaten.title}`;
    acts.appendChild(link);
  }
  saveProgress();          // `phase` is 'over' now, so this clears it
  // Written into your games by the save above, so the level can count it.
  showLevel($('over-why'), gameId);
  if (takenUp) {
    // The one thing anybody wants after losing a lost position: the position
    // again. Named for the game rather than "play again", because "again" here
    // would be ambiguous between the historical position and a fresh board.
    act(acts, `Back to move ${takenUp.moveNo}`, () => takeUp({
      game: takenUp.id, ply: takenUp.uci.length, as: you, rung: rung.id,
    }));
    const link = document.createElement('a');
    link.className = 'ctl';
    link.href = watchUrl(takenUp.id, takenUp.moveNo);
    link.textContent = 'What actually happened';
    acts.appendChild(link);
  }
  act(acts, 'Play again', () => startGame());
  act(acts, 'A different opponent', () => toSetup());
  offerReview(acts);
  offerToKeep(acts);
  $('over').hidden = false;
  reflect();
}

/**
 * "Keep this game", and what it turns into once it has been pressed.
 *
 * WHY IT IS OFFERED AT ALL. Everything else in this panel sends you somewhere:
 * play again, pick somebody else, watch the real game you just beat a copy of.
 * The game you actually played was the one thing that ended here — and it is the
 * only game on this site the visitor has any stake in. `keep.js` writes it into
 * the same store a dropped PGN lands in, so it gets a row in the library and
 * plays in the 3D player like any other game.
 *
 * WHY THE BUTTON REPLACES ITSELF WITH A LINK. Keeping is not repeatable: press it
 * twice and there are two copies of one game in the library, which is a thing
 * nobody wants and which the visitor would have to go and undo. So the button
 * becomes the answer to "where did it go" — a link straight to the game on the
 * board — and the second press is impossible rather than merely discouraged.
 */
function offerToKeep(acts) {
  // A game nobody played is not a game to keep. Resigning on move one is, though:
  // the score is the score.
  if (!live.ply) return;

  const details = () => ({
    moves: live.game.moves.slice(0, live.ply),
    result: (live.outcome?.result) || '*',
    white: you === 'w' ? 'You' : rung.name,
    black: you === 'w' ? rung.name : 'You',
    event: `Played against ${rung.name}`,
    fileName: `Your game vs ${rung.name}`,
  });

  const keep = document.createElement('button');
  keep.type = 'button';
  keep.className = 'ctl';
  keep.textContent = 'Keep this game';
  keep.title = 'Store it in your library and watch it on the board';
  keep.addEventListener('click', async () => {
    keep.disabled = true;
    keep.textContent = 'Keeping…';
    try {
      const kept = await keepGame(details());
      count('play-keep', rung.id, live.ply);
      const link = document.createElement('a');
      link.className = 'ctl primary';
      link.href = kept.url;
      link.textContent = 'Watch your game';
      link.title = `${kept.title} is in your library now`;
      keep.replaceWith(link);
    } catch (err) {
      // The two real ones are a full store and a browser with no IndexedDB. Both
      // are said out loud: the score is still on the board and Copy PGN still
      // works, so a visitor who is told has somewhere to go.
      keep.disabled = false;
      keep.textContent = 'Keep this game';
      table.say(`It could not be kept: ${err.message}`);
    }
  });
  acts.appendChild(keep);

  const copy = document.createElement('button');
  copy.type = 'button';
  copy.className = 'ctl';
  copy.textContent = 'Copy PGN';
  copy.title = 'The score, for any other chess program';
  copy.addEventListener('click', async () => {
    const ok = await copyPgn(details());
    copy.textContent = ok ? 'Copied' : 'Press Ctrl+C';
    if (ok) setTimeout(() => { copy.textContent = 'Copy PGN'; }, 1600);
  });
  acts.appendChild(copy);
}

/**
 * "Review this game": the game you just played in the player, with the engine's
 * review of it. It is already in your games (`recordGame`), so this is only a
 * link; the page it goes to lists every other one too.
 */
function offerReview(acts) {
  const record = gameId && getGame(gameId);
  if (!record || record.moves.length < 2) return;
  const link = document.createElement('a');
  link.className = 'ctl';
  link.textContent = 'Review this game';
  link.title = 'Watch it back, with the engine’s review. It is kept in Your games.';
  acts.appendChild(link);
  reviewUrl(record).then((href) => { link.href = href; }).catch(() => link.remove());
}

/** Take back a whole move — yours and theirs — so it is your turn again. */
async function takeBack() {
  if (phase === 'setup' || thinking || !live.ply) return;
  if (opponent) opponent.stop();
  // Never back past the position this page was opened on. Taking a move back is
  // undoing YOUR move; the forty half-moves Anderssen and Kieseritzky played
  // before you sat down are not yours to undo, and a board that let you rewind
  // into them would quietly turn a what-if into a different game.
  const floor = takenUp ? takenUp.uci.length : 0;
  const n = Math.max(floor, live.turn === you ? Math.max(0, live.ply - 2) : live.ply - 1);
  if (n === live.ply) return;
  tookBack += 1;
  const score = live.uci().slice(0, n);
  $('over').hidden = true;
  table.explainMate(false);
  table.state = 'playing';
  await table.setScore(score);
  phase = 'yours';
  // A take-back changes the score, so the save has to follow it — otherwise
  // closing the tab after taking a move back brings the move back too.
  saveProgress();
  table.say('');
  reflect();
}

function resign() {
  if (phase === 'setup' || phase === 'over') return;
  live.outcome = {
    over: true,
    result: you === 'w' ? '0–1' : '1–0',
    by: 'you resigned',
    winner: you === 'w' ? 'b' : 'w',
  };
  finish();
}

// ------------------------------------------------------------------- the setup

function toSetup() {
  if (opponent) opponent.stop();
  takenUp = null;
  phase = 'setup';
  table.state = 'setup';
  $('ladder').hidden = false;
  $('moves').hidden = true;
  $('over').hidden = true;
  // New game after a game against the computer: straight to its levels, the
  // choice of who having been made once already.
  showStep('computer');
  offerResume();
  reflect();
}

/** Start a game, which is also how a rematch starts. */
async function startGame() {
  count('play-start', rung.id);
  // A fresh board is not a position out of a game, whatever the URL still says.
  takenUp = null;
  gameId = newGameId('engine');
  tookBack = 0;
  table.you = you;
  table.opponentName = rung.name;
  table.state = 'playing';
  table.explainMate(false);
  await table.setScore([]);
  $('ladder').hidden = true;
  $('over').hidden = true;
  $('moves').hidden = false;

  // The board turns to the side you are playing, which is the one piece of
  // orientation a chess page owes you.
  await table.faceYourSide();

  phase = you === 'w' ? 'yours' : 'theirs';
  reflect();

  try {
    if (!opponent) {
      table.say('Starting the engine…');
      opponent = new Opponent();
      await opponent.start();
    }
    opponent.setRung(rung);
    opponent.newGame();
    table.say('');
  } catch (err) {
    table.say(`The engine did not start: ${err.message}`);
    return;
  }
  if (phase === 'theirs') theirTurn();
}

/**
 * Pick up an unfinished game where it was left.
 *
 * Deliberately the same sequence as `startGame` and not a variation on it: the
 * one difference is the score handed to `table.setScore`, and whose turn that
 * leaves it. Written as a separate function that repeats four lines rather than a
 * flag threaded through `startGame`, because the two READ differently — starting
 * a game counts a `play-start` and turns the board to your side, and resuming one
 * must do neither of those twice.
 */
async function resumeGame(saved) {
  rung = rungById(saved.rung);
  you = saved.you;
  // The same game, carried on: its own id, so your games list shows one game
  // further along rather than two. A save from before games had ids gets one.
  gameId = saved.id || newGameId('engine');
  tookBack = Number(getGame(gameId)?.takebacks) || 0;
  markRungs();
  markSides();

  table.you = you;
  table.opponentName = rung.name;
  table.state = 'playing';
  table.explainMate(false);
  await table.setScore(saved.moves);
  $('ladder').hidden = true;
  $('over').hidden = true;
  $('moves').hidden = false;
  await table.faceYourSide();

  // WHOSE TURN IT IS COMES OFF THE BOARD, not out of the save. `live.turn` is
  // what the position says after the moves have been replayed, and it cannot be
  // wrong; a stored `phase` could be, and would put the engine and the player
  // both waiting for the other one.
  phase = live.turn === you ? 'yours' : 'theirs';
  // Now the game on the board, and the one this page offers back next time.
  saveProgress();
  reflect();

  try {
    if (!opponent) {
      table.say('Starting the engine…');
      opponent = new Opponent();
      await opponent.start();
    }
    opponent.setRung(rung);
    opponent.newGame();
    table.say('');
  } catch (err) {
    table.say(`The engine did not start: ${err.message}`);
    return;
  }
  if (phase === 'theirs') theirTurn();
}

/**
 * Take up a position out of a famous game and play it on.
 *
 * WHAT THIS IS FOR
 * The commonest thought anybody has while watching a master game is not about the
 * move that was played. It is *could I have held that?* — and until this existed
 * the site answered it by changing the subject. Both of the big platforms can
 * start a game from a position and both make you go through a board editor to do
 * it, which is a different activity with the same name: you leave the game you
 * were looking at, set the position up by hand, and arrive somewhere that has
 * forgotten where it came from. This arrives knowing.
 *
 * WHO IS ACROSS THE TABLE, WHICH IS THE PART WORTH GETTING RIGHT
 * The ladder is named after people, and six of those people are IN the game you
 * have just taken up a position from. So the opponent is chosen by looking for
 * the rung named after whoever was actually sitting opposite: take up the
 * Immortal Game as Anderssen and Kieseritzky answers you, take it up as
 * Kieseritzky and Anderssen does. That is the whole point of having named
 * opponents rather than a number from one to eight, and it is two lines because
 * `LADDER` already carries the names.
 *
 * DELIBERATELY NOT A `startGame` WITH A FLAG. It reads differently — this counts
 * no `play-start`, it must not turn the board to your side before the position is
 * on it, and it has a floor under `takeBack` that a fresh game does not. The four
 * repeated lines are cheaper than a parameter threaded through a function that
 * would then be doing two jobs. Same argument as `resumeGame`, one door along.
 *
 * Returns false for a link that cannot be honoured, which leaves the setup panel
 * exactly as it was — the page working normally rather than an error to read.
 */
async function takeUp(want) {
  const at = positionFor(want);
  if (!at) return false;

  takenUp = at;
  gameId = newGameId('engine');
  tookBack = 0;
  you = want.as;
  rung = rungById(want.rung || facing(at, you) || rung.id);
  markRungs();
  markSides();

  table.you = you;
  table.opponentName = rung.name;
  table.state = 'playing';
  table.explainMate(false);
  await table.setScore(at.uci);
  $('ladder').hidden = true;
  $('over').hidden = true;
  $('moves').hidden = false;
  const slot = $('resume');
  if (slot) slot.hidden = true;
  await table.faceYourSide();

  // WHOSE TURN IT IS COMES OFF THE BOARD, exactly as it does in `resumeGame`:
  // `live.turn` is what the position says after the score has been replayed, and
  // the `as` in the link is a claim about it that a stale link could get wrong.
  phase = live.turn === you ? 'yours' : 'theirs';
  // Saved like any other game in progress, so closing the tab on a position you
  // are three moves into does not lose it. What is stored is the whole score
  // including the historical prefix — see `saveProgress` — so resuming puts the
  // same board back. It resumes as an ordinary game rather than as a taken-up
  // one, which costs the take-back floor and the "back to move 21" button, and
  // is a smaller loss than the position.
  saveProgress();
  reflect();

  try {
    if (!opponent) {
      table.say('Starting the engine…');
      opponent = new Opponent();
      await opponent.start();
    }
    opponent.setRung(rung);
    opponent.newGame();
    table.say('');
  } catch (err) {
    table.say(`The engine did not start: ${err.message}`);
    return true;
  }
  if (phase === 'theirs') theirTurn();
  return true;
}

/**
 * The id of the rung named after whoever was sitting opposite in that game.
 *
 * Matched on the name and not on the rung's own `game` field, which is the game
 * that person WON: `LADDER`'s Kieseritzky rung points at the Immortal Game
 * because that is the game he is known for, and he lost it. Asking "who is across
 * the table from me" and asking "whose game is this" give different answers here,
 * and this is the first question.
 */
function facing(at, side) {
  const name = side === 'w' ? at.black : at.white;
  return LADDER.find((entry) => entry.name === name)?.id || null;
}

/**
 * The offer to resume, at the top of the setup panel — or nothing at all.
 *
 * WHY IT IS AN OFFER AND NOT AUTOMATIC. Resuming without being asked is the wrong
 * default for a page somebody may have opened precisely because they wanted a
 * fresh game, and it would put a position on the board that the visitor has to
 * work out before they can do anything about it. The ladder stays exactly where
 * it was; this is one line above it saying a game is waiting, with the move
 * number, so it can be recognised rather than merely accepted.
 */
function offerResume() {
  const slot = $('resume');
  if (!slot) return;
  const saved = readProgress();
  slot.innerHTML = '';
  if (!saved) {
    // No game on this page to pick up — but there may be older unfinished ones,
    // and those are one link away rather than forgotten.
    const waiting = allGames().filter((r) => !r.over && r.kind === 'engine').length;
    if (!waiting) { slot.hidden = true; return; }
    const line = document.createElement('p');
    line.className = 'resume-line';
    line.innerHTML = `You have ${waiting === 1 ? 'an unfinished game' : `${waiting} unfinished games`} in <a href="../my-games/">Your games</a>.`;
    slot.appendChild(line);
    slot.hidden = false;
    return;
  }

  const moveNo = Math.floor(saved.moves.length / 2) + 1;
  const entry = rungById(saved.rung);
  // A card, as the front page's "Pick up where you left off" draws the same
  // game: the engine's face, who and where, then the choice.
  const head = document.createElement('div');
  head.className = 'resume-head';
  head.innerHTML = `<span class="resume-face" aria-hidden="true">${ENGINE_FACE}</span>`;
  const text = document.createElement('div');
  text.className = 'resume-text';
  const kicker = document.createElement('span');
  kicker.className = 'resume-kicker';
  kicker.textContent = 'Unfinished game';
  const line = document.createElement('p');
  line.className = 'resume-line';
  line.textContent = `You have a game against ${entry.name} going, at move ${moveNo}.`;
  const who = document.createElement('span');
  who.className = 'resume-who';
  who.textContent = entry.name;
  const where = document.createElement('span');
  where.className = 'resume-where';
  where.textContent = `You are ${saved.you === 'w' ? 'White' : 'Black'} · Move ${moveNo}`;
  line.replaceChildren(who, where);
  text.append(kicker, line);
  head.appendChild(text);
  slot.appendChild(head);

  const acts = document.createElement('div');
  acts.className = 'resume-acts';
  const go = document.createElement('button');
  go.type = 'button';
  go.className = 'ctl primary';
  go.innerHTML = '<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M4.5 2.8v10.4L13 8z"></path></svg>';
  go.append('Pick it up');
  go.addEventListener('click', () => { slot.hidden = true; resumeGame(saved); });
  acts.appendChild(go);

  const drop = document.createElement('button');
  drop.type = 'button';
  drop.className = 'ctl';
  drop.textContent = 'Forget it';
  drop.addEventListener('click', () => {
    keep(SAVED_KEY, '');
    // Not offered again, here or in Your games — but still there to review, as
    // a game that ended where it was left.
    const record = saved.id && getGame(saved.id);
    if (record && !record.over) saveGame({ ...record, over: true, by: 'left unfinished' });
    slot.hidden = true;
    offerResume();
  });
  acts.appendChild(drop);
  slot.appendChild(acts);
  slot.hidden = false;
}

/** Levels 1 to 10 in words, for the start card: what 750, 1000 … 3000 Elo plays like. */
const STRENGTH = [
  'Beginner', 'Novice', 'Casual player', 'Club player', 'Strong club player',
  'Expert', 'Master', 'Grandmaster', 'World class', 'Full strength',
];
const ENGINE_FACE = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="6" y="6" width="12" height="12" rx="2.5"></rect><path d="M10 10h4v4h-4zM9 2.5V6M15 2.5V6M9 18v3.5M15 18v3.5M2.5 9H6M2.5 15H6M18 9h3.5M18 15h3.5"></path></svg>';
const initials = (name) => name.split(/\s+/).filter((w) => /^[A-Z]/.test(w)).slice(0, 2).map((w) => w[0]).join('') || name[0];

/** The ladder of opponents, and which side you take. */
function buildSetup() {
  const rungs = $('rungs');
  rungs.innerHTML = '';
  for (const entry of LADDER) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'rung';
    button.setAttribute('role', 'radio');
    button.dataset.id = entry.id;
    button.innerHTML = `
      <span class="rung-name">${escapeHtml(entry.name)}</span>
      <span class="rung-meta">
        <span class="rung-year">${entry.year ? entry.year : 'today'}</span>
        ${rungLevel(entry)}
      </span>
      <span class="rung-note">${escapeHtml(entry.note)}</span>`;
    button.addEventListener('click', () => {
      rung = entry;
      keep(RUNG_KEY, entry.id);
      markRungs();
      // Picked below the card that starts the game, so back up to it: it now
      // names who was picked, and Start the game is on it.
      $('start-card').scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    rungs.appendChild(button);
  }

  // Level 1 to 10, on the card: the engine by number, beside the named players.
  const levels = $('levels');
  levels.innerHTML = '';
  for (const entry of LEVELS) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'lvl';
    button.setAttribute('role', 'radio');
    button.setAttribute('aria-label', `Level ${entry.level}`);
    button.title = entry.eloEst;
    button.dataset.id = entry.id;
    button.textContent = String(entry.level);
    button.addEventListener('click', () => {
      rung = entry;
      keep(RUNG_KEY, entry.id);
      markRungs();
    });
    levels.appendChild(button);
  }
  markRungs();

  const sides = $('sides');
  sides.innerHTML = '';
  for (const [value, label] of [['w', 'White'], ['b', 'Black']]) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'side';
    button.setAttribute('role', 'radio');
    button.dataset.side = value;
    button.innerHTML = `<span class="disc ${value}" aria-hidden="true"></span>${label}`;
    button.addEventListener('click', () => { you = value; markSides(); });
    sides.appendChild(button);
  }
  markSides();
}

function markRungs() {
  for (const button of [...$('rungs').children, ...$('levels').children]) {
    const on = button.dataset.id === rung.id;
    button.classList.toggle('on', on);
    button.setAttribute('aria-checked', String(on));
  }
  // The track fills up to the level chosen, and stays empty for a named player.
  $('levels').style.setProperty('--n', String(rung.level || 0));
  $('start-against').innerHTML = `Against <b>${escapeHtml(rung.name)}</b>`;
  // How strong, in words: a numbered level by what that Elo plays like, a named
  // player by their place on the same scale.
  $('start-strength').textContent = rung.level
    ? `${STRENGTH[rung.level - 1]} · ${rung.eloEst.replace('~', 'about ')}`
    : [RUNG_LEVEL[rung.id] != null ? `Level ${RUNG_LEVEL[rung.id].toFixed(1)}` : null, rung.eloEst.replace('~', 'about ')].filter(Boolean).join(' · ');
  $('start-face').innerHTML = rung.level ? ENGINE_FACE : escapeHtml(initials(rung.name));
  const game = rung.game ? BY_ID.get(rung.game) : null;
  $('ladder-note').textContent = game
    ? `Beat ${rung.name} and their own game is waiting: ${game.title}, ${game.date}.`
    : 'No historical game behind this one — it is the engine itself.';
}

function markSides() {
  for (const button of $('sides').children) {
    const on = button.dataset.side === you;
    button.classList.toggle('on', on);
    button.setAttribute('aria-checked', String(on));
  }
}

// ------------------------------------------------------------- what it all says

// The line under the board names the opponent in one view and asks you to pick
// one in the other, so it has to be re-said when the view changes under it.
onMode(() => reflect());

function reflect() {
  $('setup').hidden = phase !== 'setup';
  // Nothing to start over while one is being set up: Start the game is the way on.
  $('new-game').hidden = phase === 'setup';
  table.setPills(phase === 'setup' ? [] : [
    rung.name,
    you === 'w' ? 'You are White' : 'You are Black',
    // Where the position came from, on a game that was taken up rather than
    // started. Third and last, because it is the one that stops being news: it
    // says the same thing for the whole game while the other two change.
    takenUp ? `${takenUp.title}, move ${takenUp.moveNo}` : null,
  ].filter(Boolean));
  table.setTag(
    phase === 'setup' ? '' : phase === 'over' ? 'game over'
      : thinking ? 'thinking' : phase === 'yours' ? 'your move' : '',
    thinking,
  );
  table.setLead(
    // The simple view has no ladder and no colour chooser on the screen — see
    // `data-view` in `controls.css` — so "choose an opponent" would be an
    // instruction with nothing to carry it out on. It names the opponent it has
    // already chosen instead, which is the same sentence's other half.
    phase === 'setup' ? (isSimple()
      ? `Playing ${rung.name}, as White`
      : 'Choose an opponent to begin')
      : phase === 'over' ? 'The game is over'
        : phase === 'yours' ? 'Click one of your men, then where it goes'
          : `${rung.name} is thinking`,
  );
  $('take-back').disabled = phase === 'setup' || phase === 'over' || thinking
    || live.ply <= (takenUp ? takenUp.uci.length : 0);
  $('resign').disabled = phase === 'setup' || phase === 'over';
}

function act(into, label, onClick) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'ctl';
  button.textContent = label;
  button.addEventListener('click', onClick);
  into.appendChild(button);
}

function capitalise(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

$('new-game').addEventListener('click', () => toSetup());
$('begin').addEventListener('click', () => startGame());
$('take-back').addEventListener('click', () => takeBack());
$('resign').addEventListener('click', () => {
  if (confirm('Resign this game?')) resign();
});
addEventListener('keydown', (event) => {
  // Ctrl+Z is the browser's undo, not a request to take a move back.
  if (event.ctrlKey || event.metaKey || event.altKey) return;
  if (event.target.matches('input, textarea')) return;
  if (event.key === 'z' || event.key === 'Z') takeBack();
});

buildSetup();
offerResume();
reflect();

/**
 * A link that names a position out of a famous game, if this page was opened by
 * one. Awaited before anything else can be pressed, so a visitor never sees the
 * setup panel flash past on the way to the board they asked for.
 */
const wanted = readPlayOn(location.search);
if (wanted) await takeUp(wanted);
else await continueFromLink();

/**
 * `?continue=<id>`: a game out of your games list, carried on from where it
 * stopped. An engine game against the same opponent; a friend's game whose room
 * has closed, against whoever is chosen on the ladder — it becomes an engine
 * game from here, under the same id, so the list shows it once.
 *
 * The score is replayed before it is trusted, like everything else read out of
 * `localStorage`, and a link that cannot be honoured leaves the setup panel as
 * it was.
 */
async function continueFromLink() {
  const id = new URLSearchParams(location.search).get('continue');
  const record = id ? getGame(id) : null;
  if (!record || record.over || !record.moves.length) return false;
  let uci;
  try {
    uci = replay(record.moves).plies.map((p) => uciOf(p.move));
  } catch {
    return false;
  }
  const same = record.kind === 'engine' && rungById(record.rung).id === record.rung;
  await resumeGame({
    id: record.id,
    rung: same ? record.rung : rung.id,
    you: record.you === 'b' ? 'b' : 'w',
    moves: uci,
  });
  const slot = $('resume');
  if (slot) slot.hidden = true;
  return true;
}

// Handy from devtools, and what `scripts/check-play.mjs` drives the page through.
window.__play = {
  live,
  get stage() { return table.stage; },
  // The board's camera. Not the stage's: it is mounted on the canvas and outlives
  // the stages a resize builds over each new scene — see `table.js`.
  get camera() { return table.camera; },
  get engine() { return table.engine; },
  get phase() { return phase; },
  get you() { return you; },
  restFrame: () => restFrame(table.engine.ctx, live.ply),
  setRung: (id) => { rung = rungById(id); markRungs(); },
  setSide: (side) => { you = side; markSides(); },
  start: startGame,
  resume: resumeGame,
  takeUp,
  get takenUp() { return takenUp; },
  saved: readProgress,
  offerResume,
  click: (square) => table.click(square),
  get picked() { return table.picked; },
  // Where the KEYBOARD is looking, which is a different thing from `picked` and
  // has to be observable on its own. The cursor is drawn for a keyboard and not
  // for a mouse — see the `focus` handler in `table.js` — and a test can only
  // hold that line if it can ask which of the two just happened.
  get cursor() { return table.cursor; },
  moveByUci: (text) => {
    const mv = live.fromUci(text);
    if (!mv) throw new Error(`${text} is not legal here`);
    return makeMove(mv);
  },
  uciOf,
};
